import React, { useEffect } from "react";
import { NavLink, Redirect, Route, Switch, useParams } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import { useTranslation } from "utilities/i18n";
import { Card, Field, makeColumnContentForCard } from "collections/Cards";
import { Section } from "collections/Section";
import {
  TableBody,
  TableBulkItemRow,
  TableHeaderItem
} from "collections/Table";
import { useGetEntitlementQuery, useGetInstanceQuery } from "generated/graphql";
import {
  EntitlementModel,
  InstanceModel,
  newEntitlementModel,
  newInstanceModel
} from "utilities/licenseServerUtils";
import { handleNullFields } from "utilities/stringUtils";
import { GeneralInstanceSettings } from "../Settings/GeneralInstanceSettings";
import { LoadingIndicator } from "elements";
import { useHistory } from "react-router";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export const InstanceSettingsRouter: React.FC<{ id: string }> = ({ id }) => {
  return (
    <Switch>
      <Route nest path="/general">
        <GeneralInstanceSettings id={id} />
      </Route>
      <Route>
        <Redirect nest to="/general" />
      </Route>
    </Switch>
  );
};

const EntitlementsTable: React.FC<{
  instance: InstanceModel | undefined;
}> = ({ instance }) => {
  const { t } = useTranslation();
  const columns = [
    {
      key: "name",
      label: t("NAME"),
      className: "entitlement-name"
    },
    {
      key: "version",
      label: t("VERSION_ALT"),
      className: "entitlement-version"
    },
    {
      key: "status",
      label: t("STATUS"),
      className: "entitlement-status"
    },
    {
      key: "value",
      label: t("INSTANCE_ATTRIBUTES_ASSIGNED"),
      className: "entitlement-attribute-assigned"
    },
    {
      key: "expires",
      label: t("EXPIRES"),
      className: "entitlement-expires"
    }
  ];

  const entitlementCount = instance?.allEntitlements.length ?? 0;

  return (
    <>
      <Section>
        <Section.Header>
          {t("ENTITLEMENT_COUNT", { count: entitlementCount })}
        </Section.Header>
      </Section>
      <div className="table-container">
        <table className="table custom-table">
          <thead>
            <tr>
              {columns.map(({ key, label, className }) => (
                <TableHeaderItem
                  fieldName={key}
                  title={label}
                  key={key}
                  className={className}
                />
              ))}
            </tr>
          </thead>
          <TableBody
            loading={false}
            cols={columns.length - 1}
            accommodateScrollbar
          >
            {instance?.allEntitlements.map(entitlementRecordId => {
              return (
                <EntitlementsTableRow
                  recordId={entitlementRecordId}
                  instanceRecordId={instance?.recordId}
                  key={entitlementRecordId}
                />
              );
            })}
          </TableBody>
        </table>
      </div>
    </>
  );
};

const EntitlementsTableRow: React.FC<{
  recordId: string;
  instanceRecordId: string;
}> = ({ recordId, instanceRecordId }) => {
  const { t } = useTranslation();

  const { data } = useGetEntitlementQuery({
    variables: {
      recordId: recordId
    }
  });

  const entitlement: EntitlementModel | undefined = data?.entitlement
    ? newEntitlementModel(data.entitlement)
    : undefined;

  const stateText = entitlement?.isPendingAssignmentToInstance(instanceRecordId)
    ? t("ENTITLEMENT_PENDING_ASSIGNMENT_STATE", {
        state: entitlement?.state.text
      })
    : entitlement?.state.text;

  return (
    <>
      <TableBulkItemRow
        key={entitlement?.recordId}
        showChecks={false}
        onCheck={() => {}}
        checked={false}
      >
        <td>{entitlement?.name.text}</td>
        <td>{entitlement?.productVersion.text}</td>
        <td>{stateText}</td>
        <td>{entitlement?.value.text}</td>
        <td>{entitlement?.expires.text}</td>
      </TableBulkItemRow>
    </>
  );
};

export const InstanceDetails: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const tenantId = useTenantId();

  const { data, fetching } = useGetInstanceQuery({
    variables: {
      recordId: id ?? ""
    }
  });

  const instance: InstanceModel | undefined = data?.getInstance
    ? newInstanceModel(data.getInstance)
    : undefined;

  const fields: Field[] = [
    {
      key: "serialNumber",
      label: t("SERIAL_NUMBER"),
      content: <p>{handleNullFields(instance?.serialNumber.text)}</p>
    },
    {
      key: "type",
      label: t("TYPE"),
      content: <p>{handleNullFields(instance?.productId.text)}</p>
    },
    {
      key: "version",
      label: t("VERSION_ALT"),
      content: <p>{handleNullFields(instance?.productVersion.text)}</p>
    },
    {
      key: "registrationType",
      label: t("PURCHASED_PRODUCT_REGISTRATION_TYPE"),
      content: (
        <p>
          {t(
            instance?.useAlternateLicencing.data
              ? "PURCHASED_PRODUCT_REGISTRATION_TYPE_RESTRICTIVE"
              : "PURCHASED_PRODUCT_REGISTRATION_TYPE_STANDARD"
          )}
        </p>
      )
    },
    {
      key: "lastLicensedTime",
      label: t("LAST_DETECTED"),
      content: <p>{handleNullFields(instance?.lastLicensedTime.text)}</p>
    },
    {
      key: "lastModifiedTime",
      label: t("LAST_MODIFIED"),
      content: <p>{handleNullFields(instance?.lastModifiedTime.text)}</p>
    },
    {
      key: "firstDetected",
      label: t("FIRST_DETECTED"),
      content: <p>{handleNullFields(instance?.lastModifiedTime.text)}</p>
    }
  ];

  useEffect(() => {
    if (!fetching && tenantId) {
      if (
        !instance ||
        id !== instance.recordId ||
        tenantId !== instance.tenantId
      ) {
        history.push("/manage/licensing/clariti/purchased-products");
      }
    }
  }, [fetching, tenantId, id, instance, history]);

  const { Content } = Layout;

  if (!instance || fetching) {
    return (
      <Content>
        <LoadingIndicator />
      </Content>
    );
  }

  return (
    <Content className="instances-details" data-testid="instances-details">
      <Content.Title>
        {instance.productId.text}{" "}
        <p className="mb-0">{`| ${instance.name.text}`}</p>
      </Content.Title>
      <Section>
        <Section.Header>{t("PRODUCT_INFORMATION")}</Section.Header>
        <Card className="table-card full-width">
          {makeColumnContentForCard(fields, 4)}
        </Card>
      </Section>
      <EntitlementsTable instance={instance} />
    </Content>
  );
};

export const InstanceDetailsRouter: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { Sidebar } = Layout;

  const { data } = useGetInstanceQuery({
    variables: {
      recordId: id ?? ""
    }
  });

  const enableSettings = data?.getInstance?.useAlternateLicencing ?? false;

  return (
    <>
      <Sidebar>
        <span className="title-capitalized">{t("CLARITI")}</span>
        <ul className="menu router">
          <li>
            <NavLink nest exact to={""}>
              {t("OVERVIEW")}
            </NavLink>
          </li>
          <li>
            {enableSettings ? (
              <>
                <NavLink nest exact={false} to="/settings">
                  {t("SETTINGS")}
                </NavLink>
                <Route nest path="/settings">
                  <ul className="menu router sub">
                    <li>
                      <NavLink nest exact to="/general">
                        {t("GENERAL")}
                      </NavLink>
                    </li>
                  </ul>
                </Route>
              </>
            ) : (
              <span className="nav-link-disabled">{t("SETTINGS")}</span>
            )}
          </li>
        </ul>
      </Sidebar>
      <Switch>
        <Route nest path="/settings">
          <InstanceSettingsRouter id={id} />
        </Route>
        <Route nest path="/">
          <InstanceDetails id={id} />
        </Route>
      </Switch>
    </>
  );
};
